import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AlertTriangle, Search, Menu, X } from "lucide-react";
import AgencyMark from "./AgencyMark";

const links = [
  { to: "/", label: "Beranda" },
  { to: "/lapor", label: "Buat Laporan" },
  { to: "/lacak", label: "Lacak Laporan" },
];

export default function PublicNavbar() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile on route change
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const isHome = pathname === "/";

  return (
    <>
      <header
        data-testid="public-navbar"
        className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-500 ${
          scrolled
            ? "bg-white/95 backdrop-blur-xl shadow-[0_4px_30px_rgba(11,43,94,0.1)] border-b border-slate-200/80"
            : isHome
            ? "bg-[#061830]/70 backdrop-blur-md border-b border-white/10"
            : "bg-white/85 backdrop-blur-xl border-b border-slate-200/60"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-18">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-home-link">
              <AgencyMark className="h-12 w-12 shrink-0 transition-transform group-hover:scale-105" />
              <div className="leading-tight">
                <div className={`text-[10px] tracking-[0.22em] font-semibold uppercase transition-colors ${scrolled || !isHome ? "text-slate-500" : "text-white/60"}`}>
                  Ditresnarkoba
                </div>
                <div className={`font-display font-extrabold text-[15px] transition-colors ${scrolled || !isHome ? "text-[#0b2b5e]" : "text-white"}`}>
                  Polda Jawa Tengah
                </div>
              </div>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `relative px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                      isActive
                        ? scrolled || !isHome
                          ? "text-[#0b2b5e] bg-blue-50"
                          : "text-yellow-300 bg-white/15"
                        : scrolled || !isHome
                        ? "text-slate-600 hover:text-[#0b2b5e] hover:bg-slate-50"
                        : "text-white/80 hover:text-white hover:bg-white/10"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-2">
              {/* Mobile track shortcut */}
              <Link
                to="/lacak"
                className={`md:hidden inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 transition-colors ${scrolled || !isHome ? "text-slate-600 hover:text-[#0b2b5e]" : "text-white/80 hover:text-white"}`}
                data-testid="navbar-mobile-track"
              >
                <Search className="h-4 w-4" />
              </Link>

              {/* Lapor CTA */}
              <Link
                to="/lapor"
                data-testid="navbar-lapor-cta"
                className="btn-shimmer inline-flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-bold text-[#0b2b5e] shadow-sm hover:scale-105 transition-all"
                style={{ background: "linear-gradient(135deg, #facc15 0%, #f59e0b 100%)" }}
              >
                <AlertTriangle className="h-4 w-4" />
                <span className="hidden sm:inline">Lapor Sekarang</span>
                <span className="sm:hidden">Lapor</span>
              </Link>

              {/* Portal Petugas */}
              <Link
                to="/admin/login"
                data-testid="navbar-admin-login"
                className={`hidden sm:inline-flex items-center text-xs font-semibold px-3 py-2 rounded-lg border transition-all hover:scale-105 ${
                  scrolled || !isHome
                    ? "text-[#0b2b5e] border-[#0b2b5e]/30 bg-white hover:bg-slate-50"
                    : "text-[#0b2b5e] border-white bg-white hover:bg-white/90"
                }`}
              >
                Portal Petugas
              </Link>

              {/* Mobile hamburger */}
              <button
                className={`md:hidden inline-flex p-2 rounded-lg transition-colors ${scrolled || !isHome ? "text-slate-600" : "text-white"}`}
                onClick={() => setMobileOpen((v) => !v)}
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            mobileOpen ? "max-h-64 border-t border-slate-200/60" : "max-h-0"
          } bg-white/95 backdrop-blur-xl`}
        >
          <nav className="px-4 py-4 space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                    isActive
                      ? "text-[#0b2b5e] bg-blue-50"
                      : "text-slate-600 hover:text-[#0b2b5e] hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/admin/login"
              className="block px-4 py-3 text-sm font-semibold text-slate-500 hover:text-[#0b2b5e] rounded-lg hover:bg-slate-50 transition-colors"
            >
              Portal Petugas
            </Link>
          </nav>
        </div>
      </header>

    </>
  );
}
